import { clampMatchPosition, FilterMatch } from "../filterResult";

import styles from "./HighlightedText.module.css";

export interface HighlightedTextProps {
  /** The text to render. */
  text: string;

  /**
   * Optional match metadata. When provided, the slice of `text` indicated by
   * `match.position` is wrapped in a highlighted span.
   */
  match?: FilterMatch;

  /** Class name applied to the outer wrapper span. */
  className?: string;
}

/**
 * Renders text with an optional highlighted slice.
 *
 * Renders as a `<span>` so it can be composed inside any text-bearing element.
 * If a block-level wrapper is needed, the consumer should wrap this component.
 */
export function HighlightedText({
  text,
  match,
  className,
}: HighlightedTextProps) {
  if (!match) {
    return <span className={className}>{text}</span>;
  }

  const [start, end] = clampMatchPosition(match.position, text.length);

  // A clamped span that collapsed to zero width (e.g. malformed input
  // with `to <= from`) carries no highlight — render plain text rather
  // than an empty highlight span.
  if (start === end) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {text.slice(0, start)}
      <span className={styles.highlight}>{text.slice(start, end)}</span>
      {text.slice(end)}
    </span>
  );
}
